import GeoContext from 'contexts/geoContext'
import { useLocation, useParams } from 'react-router-dom'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import useOrder from 'hooks/Order/useOrder'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import ChatTripPlannerMobileBlocker from './ChatTripPlannerMobileBlocker'
import { getFirstName, isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import { isSpoofed as isSpoofedSelector } from 'selectors/featuresSelectors'
import { canAccessStaffChat } from 'tripPlanner/utils/staffChatAccess'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { useAnonymousChatClaiming } from './ChatTripPlannerPage/hooks/useAnonymousChatClaiming'
import { useChatHistory } from './ChatTripPlannerPage/hooks/useChatHistory'
import { NEW_CHAT_ROUTE_SEGMENT, useChatNavigation } from './ChatTripPlannerPage/hooks/useChatNavigation'
import { useChatTitleGeneration } from './ChatTripPlannerPage/hooks/useChatTitleGeneration'
import { usePromptManagement } from './ChatTripPlannerPage/hooks/usePromptManagement'
import type { PromptManagementContextValue } from './ChatTripPlannerPage/contexts/PromptManagementContext'
import TripChatRuntime from './ChatTripPlannerPage/TripChatRuntime'
import TripChatSurfaceRouter from './ChatTripPlannerPage/TripChatSurfaceRouter'
import type { TripChatDesktopSurfaceProps } from './ChatTripPlannerPage/surfaces/TripChatDesktopSurface'
import type { TripChatMobileSurfaceProps } from './ChatTripPlannerPage/surfaces/TripChatMobileSurface'
import { buildBookingTag, getFlightBookingItemIds, getFlightDetailsStateByItemIds, getOrderTileDataList } from './ChatLandingPage/bookingSelectionUtils'
import { getEffectiveOrderIds, getNextDismissedPrefilledOrderItemIds } from './ChatTripPlannerPage/utils/prefilledOrderSelection'
import { getTravelOrderOfferTypes } from 'selectors/orderSelectors'
import { getOrderTravelDurationWithFlightDetails } from 'lib/flights/flightSummaryHelpers'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

function ChatTripPlannerPage() {
  const isMobileOrTablet = useIsMobileOrTablet()
  const isTripChatMobileEnabled = useFeatureFlag(OptimizelyFeatureFlags.tripChatMobile)
  const intl = useIntl()

  const isStaff = useAppSelector(isLEStaff)
  const canManagePrompts = isStaff
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isSpoofed = useAppSelector(isSpoofedSelector)
  const spoofingAgentEmail = useAppSelector(state => state.auth.account.spoofedBy?.email)
  const accountEmail = useAppSelector(state => state.auth.account.email)
  const canViewStaffChat = isLoggedIn && isStaff && canAccessStaffChat(accountEmail)
  // Tracks the conversation a staff member opened via the "Spoof conversation"
  // form so we can surface a banner while they view a customer's chat.
  const [staffViewedChatId, setStaffViewedChatId] = useState<number | undefined>(undefined)
  const firstName = useAppSelector(getFirstName)
  const { latitude, longitude, currentRegionCode } = useContext(GeoContext)
  const { search } = useLocation()
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  const userData = useMemo(() => ({
    firstName: firstName || undefined,
    lat: latitude,
    lng: longitude,
    timezone,
    region: currentRegionCode,
  }), [firstName, latitude, longitude, timezone, currentRegionCode])

  const [isDevMode, setIsDevMode] = useState(false)
  const [bookingSelection, setBookingSelection] = useState<{
    orderIds: Array<string>
    itemIds: Array<string>
  }>({ orderIds: [], itemIds: [] })
  const [dismissedPrefilledOrderItemIds, setDismissedPrefilledOrderItemIds] = useState<Array<string>>([])
  const { chatId: routeChatIdParam } = useParams<{ chatId?: string }>()
  const orderIdFromQuery = useMemo(() => new URLSearchParams(search).get('orderId') ?? undefined, [search])
  const [orderForChip] = useOrder(orderIdFromQuery)

  const {
    chatId, setChatId: syncChatId, setChatIdForStreaming,
    isActiveChatNewlyCreated,
    isNewChatRoute, isLandingRoute, existingRouteChatId, navigateToNewChatRoute,
  } = useChatNavigation({ routeChatId: routeChatIdParam })

  const chatKey = useMemo(
    () => (chatId !== undefined ? String(chatId) : NEW_CHAT_ROUTE_SEGMENT),
    [chatId],
  )

  useEffect(() => {
    setDismissedPrefilledOrderItemIds([])
  }, [orderIdFromQuery])

  const prefilledOrderTiles = useAppSelector((state) => {
    if (!orderForChip) {
      return []
    }

    return getOrderTileDataList(
      orderForChip,
      getTravelOrderOfferTypes(state, orderForChip),
      getOrderTravelDurationWithFlightDetails(orderForChip, state.orders.flightDetails),
    )
  }, shallowEqual)

  const prefilledOrderFlightItemIds = useMemo(() => {
    return getFlightBookingItemIds(prefilledOrderTiles)
  }, [prefilledOrderTiles])

  const flightDetailsState = useAppSelector((state) => {
    return getFlightDetailsStateByItemIds(state.orders.flightDetails, prefilledOrderFlightItemIds)
  }, shallowEqual)

  const prefilledOrderTagsByItemId = useMemo(() => {
    const nextPrefilledOrderTagsByItemId = new Map<string, string>()

    for (const tile of prefilledOrderTiles) {
      nextPrefilledOrderTagsByItemId.set(tile.itemId, buildBookingTag({
        intl,
        tile,
        flightDetailsState,
      }))
    }

    return nextPrefilledOrderTagsByItemId
  }, [flightDetailsState, intl, prefilledOrderTiles])

  const dismissedPrefilledOrderItemIdSet = useMemo(() => {
    return new Set(dismissedPrefilledOrderItemIds)
  }, [dismissedPrefilledOrderItemIds])

  const prefilledOrderChipLabels = useMemo(() => {
    return [...new Set(
      prefilledOrderTiles
        .filter(tile => !dismissedPrefilledOrderItemIdSet.has(tile.itemId))
        .map(tile => prefilledOrderTagsByItemId.get(tile.itemId))
        .filter((tag): tag is string => Boolean(tag)),
    )]
  }, [dismissedPrefilledOrderItemIdSet, prefilledOrderTagsByItemId, prefilledOrderTiles])

  const hasCustomizedPrefilledOrderSelection = dismissedPrefilledOrderItemIds.length > 0

  const effectiveOrderIds = useMemo(() => {
    return getEffectiveOrderIds({
      orderIdFromQuery,
      bookingSelectionOrderIds: bookingSelection.orderIds,
      hasCustomizedPrefilledOrderSelection,
    })
  }, [bookingSelection.orderIds, hasCustomizedPrefilledOrderSelection, orderIdFromQuery])

  const {
    refetchChatList, claimChatsMutation,
    updateChatLastActivityTimestamp, updateChatMetadata, startChatTitleGeneration: startTitleGen,
  } = useChatHistory({ isLoggedIn })

  const { startChatTitleGeneration } = useChatTitleGeneration({ startTitleGen })

  const {
    selectedPrompt, promptOptions, selectedPromptKey,
    loadPrompts, handlePromptAdd, handlePromptSelect,
  } = usePromptManagement({ canManagePrompts })

  const promptContextValue = useMemo<PromptManagementContextValue>(() => ({
    promptOptions, selectedPromptKey, loadPrompts, handlePromptAdd, handlePromptSelect,
  }), [promptOptions, selectedPromptKey, loadPrompts, handlePromptAdd, handlePromptSelect])

  const { waitForClaimCompletion, anonymousClaimTransfer } = useAnonymousChatClaiming({
    isLoggedIn,
    currentChatId: chatId,
    claimChatsMutation,
    refetchChatList,
  })

  const handleDevModeToggle = useCallback(() => {
    setIsDevMode((prev) => !prev)
  }, [])

  const handleRemovePrefilledOrderTag = useCallback((tag: string) => {
    setDismissedPrefilledOrderItemIds((prev) => {
      return getNextDismissedPrefilledOrderItemIds({
        tag,
        dismissedPrefilledOrderItemIds: prev,
        prefilledOrderTagsByItemId,
      })
    })
  }, [prefilledOrderTagsByItemId])

  const desktopSurfaceProps: TripChatDesktopSurfaceProps = {
    chatId,
    syncChatId,
    isActiveChatNewlyCreated,
    isNewChatRoute,
    isLandingRoute,
    existingRouteChatId,
    navigateToNewChatRoute,
    isLoggedIn,
    isDevMode,
    isSpoofed,
    spoofingAgentEmail,
    canViewStaffChat,
    staffViewedChatId,
    onStaffViewedChatIdChange: setStaffViewedChatId,
    onDevModeToggle: handleDevModeToggle,
    setBookingSelection,
    selectedBookingItemIds: bookingSelection.itemIds,
    prefilledOrderChipLabels,
    onRemovePrefilledOrderTag: handleRemovePrefilledOrderTag,
  }

  const mobileSurfaceProps: TripChatMobileSurfaceProps = {
    chatId,
    syncChatId,
    isNewChatRoute,
    isLandingRoute,
    existingRouteChatId,
    navigateToNewChatRoute,
    isLoggedIn,
    isStaff,
    isDevMode,
    isSpoofed,
    spoofingAgentEmail,
    onStaffViewedChatIdChange: setStaffViewedChatId,
    onDevModeToggle: handleDevModeToggle,
    setBookingSelection,
    selectedBookingItemIds: bookingSelection.itemIds,
    prefilledOrderChipLabels,
  }

  if (isMobileOrTablet && !isTripChatMobileEnabled) {
    return <ChatTripPlannerMobileBlocker />
  }

  return (
    <TripChatRuntime
      promptContextValue={promptContextValue}
      chatId={chatId}
      setChatIdForStreaming={setChatIdForStreaming}
      prompt={selectedPrompt.promptId}
      isDevMode={isDevMode}
      isLoggedIn={isLoggedIn}
      orderIds={effectiveOrderIds}
      refetchChatList={refetchChatList}
      waitForAnonymousChatClaim={waitForClaimCompletion}
      anonymousClaimTransfer={anonymousClaimTransfer}
      startChatTitleGeneration={startChatTitleGeneration}
      updateChatLastActivityTimestamp={updateChatLastActivityTimestamp}
      updateChatMetadata={updateChatMetadata}
      userData={userData}
      chatKey={chatKey}
    >
      {isMobileOrTablet && (
        <TripChatSurfaceRouter
          surface="mobile"
          mobileSurfaceProps={mobileSurfaceProps}
        />
      )}
      {!isMobileOrTablet && (
        <TripChatSurfaceRouter
          surface="desktop"
          desktopSurfaceProps={desktopSurfaceProps}
        />
      )}
    </TripChatRuntime>
  )
}

export default ChatTripPlannerPage
